import {
  AppIconName,
  IcebergIcon,
  IconName,
  TjenestegruppeIconName,
  useLocalStorage,
} from '@tradesolution/iceberg-ui-react';
import { Fragment, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import './index.scss';

export interface SubLink {
  label: string;
  link: string;
  icon?: IconName | TjenestegruppeIconName | AppIconName;
}

export interface NavLink {
  label: string;
  link: string;
  icon?: IconName | TjenestegruppeIconName | AppIconName;
  roles?: string[]; // which roles are allowed to see this link
  subLinks?: SubLink[];
}

export interface SidebarProps {
  navLinks: NavLink[];
}

const Sidebar = ({ navLinks }: SidebarProps) => {
  const location = useLocation();
  const [sidebarExpanded, setSidebarExpanded] = useLocalStorage<boolean>('sidebar-expanded', true);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  const sidebarWidth = sidebarExpanded ? '250px' : '55px';

  const toggleSubmenu = (label: string) => {
    setExpandedMenus((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  };

  const hasActiveSubLink = (l: NavLink) =>
    l.subLinks?.some((sub) => sub.link === location.pathname) ?? false;

  return (
    <div
      className="iceberg-navbar-left d-flex flex-column flex-shrink-0"
      style={{ width: sidebarWidth, position: 'sticky', top: '58px' }}
    >
      <ul className="nav flex-column nav-pills mb-auto">
        <a
          className="nav-link"
          role="button"
          onClick={() => setSidebarExpanded(!sidebarExpanded)}
          style={{ whiteSpace: 'nowrap' }}
        >
          <span style={{ marginRight: '1rem' }}>
            {sidebarExpanded ? (
              <IcebergIcon icon="chevron-left" />
            ) : (
              <IcebergIcon icon="chevron-right" />
            )}
          </span>{' '}
          skjul
        </a>
        {navLinks.map((l) => {
          const hasSubLinks = l.subLinks && l.subLinks.length > 0;
          const isExpanded = expandedMenus.includes(l.label);
          const isParentActive = hasActiveSubLink(l);

          if (hasSubLinks) {
            return (
              <Fragment key={l.link}>
                <li>
                  <a
                    className={`nav-link${isParentActive ? ' active' : ''}`}
                    role="button"
                    onClick={() => toggleSubmenu(l.label)}
                    title={l.label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ marginRight: '1rem' }}>
                      <IcebergIcon icon={l.icon as any} />
                    </span>
                    {sidebarExpanded && (
                      <>
                        <span style={{ flex: 1 }}>{l.label}</span>
                        <IcebergIcon icon={isExpanded ? 'chevron-up' : 'chevron-down'} />
                      </>
                    )}
                  </a>
                </li>
                {isExpanded &&
                  sidebarExpanded &&
                  l.subLinks!.map((sub) => (
                    <li
                      key={sub.link}
                      style={{
                        paddingLeft: '1rem',
                        borderLeft: '3px solid var(--bs-border-color)',
                        marginLeft: '1rem',
                      }}
                    >
                      <Nav.Link
                        as={Link}
                        to={sub.link}
                        title={sub.label}
                        className={sub.link === location.pathname ? 'active' : ''}
                        style={{ fontSize: '0.9em' }}
                      >
                        <span style={{ marginRight: '0.75rem' }}>
                          <IcebergIcon icon={sub.icon as any} />
                        </span>
                        <span>{sub.label}</span>
                      </Nav.Link>
                    </li>
                  ))}
              </Fragment>
            );
          }

          return (
            <li key={l.link}>
              <Nav.Link
                as={Link}
                to={l.link}
                title={l.label}
                className={l.link === location.pathname ? 'active' : ''}
              >
                <span style={{ marginRight: '1rem' }}>
                  <IcebergIcon icon={l.icon as any} />
                </span>{' '}
                <span>{l.label}</span>
              </Nav.Link>
            </li>
          );
        })}
      </ul>
      <ul className="nav flex-column nav-pills">
        <li>
          <a className="nav-link" href="http://tradesolution.no" style={{ marginBottom: 'auto' }}>
            <IcebergIcon icon="sign" color="#003b6e" />
          </a>
        </li>
      </ul>
    </div>
  );
};

export default Sidebar;
